import { FC, useEffect } from 'react';
import { Navigate } from 'react-router';

import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { useQuery } from 'hooks/useQuery';
import { useTranslations } from 'hooks/useTranslations';
import { pushGTMEvent } from 'utils/analyticsUtils';
import * as routes from 'router/routes';
import { signUpSelector, verifyCustomerAccount } from 'store/signupState';
import { showInfoToast, showSuccessToast } from 'utils/toastUtils';

import Loader from 'components/UI/Loader';
import PageImageWrapper from 'components/UI/Page/PageImageWrapper';

const EmailVerificationPage: FC = () => {
  const t = useTranslations();
  const dispatch = useAppDispatch();
  const {
    isVerified,
    data: {
      accountStatus: { emailVerified }
    },
    error
  } = useAppSelector(signUpSelector);

  // Params
  const [email, code] = useQuery('email', 'code');
  const isInvalid = !email && !code;

  // Verify
  useEffect(() => {
    // Email & code
    if (email && code) {
      dispatch(verifyCustomerAccount(email, code));
    }
  }, [dispatch, email, code]);

  // Success toast
  useEffect(() => {
    if (emailVerified) {
      pushGTMEvent('emailVerificationSuccess');
      showSuccessToast(t('email_verification_page.account_verified'));
    }
    if (isVerified) {
      pushGTMEvent('emailVerificationSuccess');
      showInfoToast(t('email_verification_page.account_already_verified'));
    }
  }, [emailVerified, isVerified, t]);

  if (error && email) {
    return (
      <Navigate
        to={`${routes.SIGNUP_VERIFY_EMAIL}?email=${encodeURIComponent(email)}`}
      />
    );
  }

  // Redirect to login if invalid or verified
  if (isInvalid || emailVerified || isVerified) {
    return <Navigate to={routes.LOGIN} />;
  }

  return (
    <PageImageWrapper>
      <Loader center size="large" />
    </PageImageWrapper>
  );
};

export default EmailVerificationPage;
